import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  ComparisonBasis,
  CoversTurnover,
  DailySalesSummary,
  DayEndReport,
  PaymentBreakdown,
  PeakHoursResponse,
  PeriodComparison,
  TopItemsResponse,
} from '@restaurant/api-client';
import { useQuery } from '@tanstack/react-query';
import type { DateRange } from '../lib/range';

const BASE = '/api/restaurants/{restaurantId}/analytics';

const useRid = () => useRestaurantStore((s) => s.selectedRestaurantId) ?? '';

export function useDailySales(range: DateRange) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'sales', range.from, range.to],
    queryFn: async () =>
      unwrapResult<DailySalesSummary>(
        await openapiClient.GET(`${BASE}/sales`, {
          params: { path: { restaurantId }, query: range },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function useDayEnd(date: string) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'day-end', date],
    queryFn: async () =>
      unwrapResult<DayEndReport>(
        await openapiClient.GET(`${BASE}/day-end`, {
          params: { path: { restaurantId }, query: { date } },
        }),
      ),
    enabled: !!restaurantId && !!date,
  });
}

export function usePaymentBreakdown(range: DateRange) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'payments', range.from, range.to],
    queryFn: async () =>
      unwrapResult<PaymentBreakdown>(
        await openapiClient.GET(`${BASE}/payments`, {
          params: { path: { restaurantId }, query: range },
        }),
      ),
    enabled: !!restaurantId,
  });
}

/** Covers/turnover is dine-in only - pass `enabled: false` where table ordering
 *  is switched off so the request isn't made for a surface that stays hidden. */
export function useCoversTurnover(range: DateRange, enabled = true) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'covers', range.from, range.to],
    queryFn: async () =>
      unwrapResult<CoversTurnover>(
        await openapiClient.GET(`${BASE}/covers`, {
          params: { path: { restaurantId }, query: range },
        }),
      ),
    enabled: !!restaurantId && enabled,
  });
}

export function useTopItems(range: DateRange, rankBy: 'QUANTITY' | 'REVENUE') {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'top-items', range.from, range.to, rankBy],
    queryFn: async () =>
      unwrapResult<TopItemsResponse>(
        await openapiClient.GET(`${BASE}/top-items`, {
          params: { path: { restaurantId }, query: { ...range, rankBy } },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function usePeakHours(range: DateRange) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'peak-hours', range.from, range.to],
    queryFn: async () =>
      unwrapResult<PeakHoursResponse>(
        await openapiClient.GET(`${BASE}/peak-hours`, {
          params: { path: { restaurantId }, query: range },
        }),
      ),
    enabled: !!restaurantId,
  });
}

export function usePeriodComparison(range: DateRange, compareTo: ComparisonBasis) {
  const restaurantId = useRid();
  return useQuery({
    queryKey: ['analytics', restaurantId, 'comparison', range.from, range.to, compareTo],
    queryFn: async () =>
      unwrapResult<PeriodComparison>(
        await openapiClient.GET(`${BASE}/comparison`, {
          params: { path: { restaurantId }, query: { ...range, compareTo } },
        }),
      ),
    enabled: !!restaurantId,
  });
}
